<script>
import moment from "moment";
import Datepicker from "vuejs-datepicker";

const PRESETS = [
  { key: "7d", label: "Last 7 days", amount: 7, unit: "days" },
  { key: "30d", label: "Last 30 days", amount: 30, unit: "days" },
  { key: "90d", label: "Last 90 days", amount: 90, unit: "days" },
  { key: "12m", label: "Last 12 months", amount: 12, unit: "months" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom range…" },
];

export default {
  name: "period-filter",
  components: { Datepicker },
  props: {
    value: {
      type: Object,
      default: () => ({ preset: "12m", startDate: null, endDate: null }),
    },
  },
  data() {
    return {
      presets: PRESETS,
      selectedPreset: this.value.preset || "12m",
      lastAppliedPreset: this.value.preset || "12m",
      showCustomPanel: false,
      panelAlign: "right",
      customStart: null,
      customEnd: null,
      appliedCustomLabel: "",
      customError: "",
    };
  },
  computed: {
    customOptionLabel() {
      return this.appliedCustomLabel || "Custom range…";
    },
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
  },
  methods: {
    rangeForPreset(key) {
      const preset = this.presets.find((p) => p.key === key);
      if (!preset || key === "all") {
        return { startDate: null, endDate: null };
      }
      return {
        startDate: moment().endOf("day").subtract(preset.amount, preset.unit).startOf("day").toISOString(),
        endDate: moment().endOf("day").toISOString(),
      };
    },
    openCustomPanel() {
      // Anchor the panel to whichever side of the select has room
      const host = this.$el.getBoundingClientRect();
      const panelWidth = Math.min(320, window.innerWidth - 32);
      this.panelAlign = host.left + panelWidth <= window.innerWidth - 16 ? "left" : "right";
      this.showCustomPanel = true;
    },
    onPresetChange() {
      this.customError = "";
      if (this.selectedPreset === "custom") {
        this.openCustomPanel();
        return;
      }
      this.showCustomPanel = false;
      this.appliedCustomLabel = "";
      this.lastAppliedPreset = this.selectedPreset;
      this.emitPeriod(this.rangeForPreset(this.selectedPreset));
    },
    applyCustomRange() {
      if (!this.customStart || !this.customEnd) {
        this.customError = "Pick both dates";
        return;
      }
      const start = moment(this.customStart).startOf("day");
      const end = moment(this.customEnd).endOf("day");
      if (start.isAfter(end)) {
        this.customError = "Start date must be before the end date";
        return;
      }
      this.customError = "";
      this.appliedCustomLabel = `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
      this.lastAppliedPreset = "custom";
      this.showCustomPanel = false;
      this.emitPeriod({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      });
    },
    cancelCustomRange() {
      this.showCustomPanel = false;
      this.customError = "";
      if (this.lastAppliedPreset !== "custom") {
        this.selectedPreset = this.lastAppliedPreset;
      }
    },
    onDocumentClick(event) {
      if (this.showCustomPanel && this.$el && !this.$el.contains(event.target)) {
        this.cancelCustomRange();
      }
    },
    emitPeriod(range) {
      this.$emit("input", {
        preset: this.selectedPreset,
        startDate: range.startDate,
        endDate: range.endDate,
      });
    },
  },
};
</script>

<template>
  <div class="period-filter">
    <div class="select-wrap">
      <select class="filter-input filter-select" v-model="selectedPreset" @change="onPresetChange">
        <option v-for="p in presets" :key="p.key" :value="p.key">
          {{ p.key === "custom" ? customOptionLabel : p.label }}
        </option>
      </select>
      <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="1 1 5 5 9 1" />
      </svg>
    </div>

    <button
      v-if="selectedPreset === 'custom' && !showCustomPanel"
      type="button"
      class="edit-range-btn"
      title="Edit date range"
      aria-label="Edit date range"
      @click.stop="openCustomPanel"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    </button>

    <div v-if="showCustomPanel" class="custom-panel" :class="`panel-align-${panelAlign}`">
      <div class="custom-panel-title">Custom date range</div>
      <div class="custom-fields">
        <div class="custom-field">
          <label class="custom-label">From</label>
          <datepicker
            v-model="customStart"
            placeholder="Start date"
            input-class="filter-input custom-date-input"
            wrapper-class="custom-date-wrap"
          />
        </div>
        <div class="custom-field custom-field-end">
          <label class="custom-label">To</label>
          <datepicker
            v-model="customEnd"
            placeholder="End date"
            input-class="filter-input custom-date-input"
            wrapper-class="custom-date-wrap"
          />
        </div>
      </div>
      <div v-if="customError" class="custom-error">{{ customError }}</div>
      <div class="custom-actions">
        <button type="button" class="custom-cancel-btn" @click="cancelCustomRange">Cancel</button>
        <button type="button" class="custom-apply-btn" @click="applyCustomRange">Apply</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.period-filter {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}
.edit-range-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  background: #fff;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 120ms ease, color 120ms ease;
}
.edit-range-btn:hover {
  border-color: #019256;
  color: #019256;
}
.filter-input {
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #1f2937;
  background: #fff;
  outline: none;
  font-family: inherit;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.filter-input:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.select-wrap {
  position: relative;
}
.filter-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 32px;
  cursor: pointer;
  min-width: 170px;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.select-caret {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}
.custom-panel {
  position: absolute;
  top: calc(100% + 8px);
  z-index: 1000;
  width: 320px;
  max-width: calc(100vw - 32px);
  padding: 16px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 16px 40px -12px rgba(8, 11, 13, 0.25);
}
.panel-align-right {
  right: 0;
}
.panel-align-left {
  left: 0;
}
.custom-panel-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 12px;
}
.custom-fields {
  display: flex;
  gap: 10px;
}
.custom-field {
  flex: 1;
  min-width: 0;
}
.custom-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 6px;
}
::v-deep .custom-date-wrap {
  display: block;
}
::v-deep .custom-date-input {
  width: 100%;
}
/* Keep the To calendar inside the panel's right edge on desktop */
.custom-field-end ::v-deep .vdp-datepicker__calendar {
  right: 0;
}
.custom-error {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 500;
  color: #b43643;
}
.custom-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}
.custom-cancel-btn {
  height: 34px;
  padding: 0 14px;
  background: transparent;
  color: #4b5563;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 120ms ease, color 120ms ease;
}
.custom-cancel-btn:hover {
  border-color: rgba(15, 23, 42, 0.25);
  color: #111827;
}
.custom-apply-btn {
  height: 34px;
  padding: 0 16px;
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.custom-apply-btn:hover {
  background: #017a48;
}
@media (max-width: 575px) {
  .custom-fields {
    flex-direction: column;
  }
  ::v-deep .vdp-datepicker__calendar {
    width: 100% !important;
    left: 0;
    right: auto !important;
  }
}
</style>
